// ─────────────────────────────────────────
// Signup.js — the sign up page
// ─────────────────────────────────────────
// Creates a new user in Supabase Auth.
// Supabase automatically handles password
// hashing and security — you never store
// raw passwords. Ever.

import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'

export default function Signup() {
  const [email, setEmail]       = useState('')
  const [password, setPassword] = useState('')
  const [username, setUsername] = useState('')
  const [handicap, setHandicap] = useState('')
  const [error, setError]       = useState(null)
  const [success, setSuccess]   = useState(false)
  const [loading, setLoading]   = useState(false)

  async function handleSignup(e) {
    e.preventDefault()
    setLoading(true)
    setError(null)

    // Step 1: Create the auth account
    const { data, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { username, handicap: parseFloat(handicap) || null }
      }
    })

    if (authError) {
      setError(authError.message)
      setLoading(false)
      return
    }

// Step 2: Create their profile row in the database
    if (data.user) {
      const inviterUsername = localStorage.getItem('looper_inviter')

      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: data.user.id,
          username,
          handicap: parseFloat(handicap) || null,
          email,
          ...(inviterUsername ? { invited_by: inviterUsername } : {})
        }, { onConflict: 'id' })

      if (profileError) {
        console.error('Profile creation error:', profileError)
      }

      // Step 3: Auto-follow the inviter if they came via an invite link
      if (inviterUsername) {
        const { data: inviterProfile } = await supabase
          .from('profiles')
          .select('id')
          .ilike('username', inviterUsername)
          .single()

        if (inviterProfile && inviterProfile.id !== data.user.id) {
          await supabase
            .from('follows')
            .insert({ follower_id: data.user.id, following_id: inviterProfile.id })
        }
        localStorage.removeItem('looper_inviter')
      }
    }

    setSuccess(true)
    setLoading(false)
  }

  if (success) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⛳</div>
          <div style={styles.logo}>You're in.</div>
          <div style={{ color: '#6aad80', fontSize: 14, marginBottom: 28 }}>
            Check your email to confirm your account, then log in.
          </div>
          <Link to="/login" style={{ ...styles.btn, display: 'block', textDecoration: 'none' }}>
            Go to Login →
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo}>BREAKIE</div>
        <div style={styles.tagline}>Join the loop. It's free.</div>

        <form onSubmit={handleSignup} style={styles.form}>
          <input
            style={styles.input}
            type="text"
            placeholder="Username (e.g. jakemgolf)"
            value={username}
            onChange={e => setUsername(e.target.value.toLowerCase().replace(/\s/g, ''))}
            required
          />
          <input
            style={styles.input}
            type="number"
            placeholder="Handicap (e.g. 8.4)"
            step="0.1"
            min="0"
            max="54"
            value={handicap}
            onChange={e => setHandicap(e.target.value)}
          />
          <input
            style={styles.input}
            type="email"
            placeholder="Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
          <input
            style={styles.input}
            type="password"
            placeholder="Password (min 6 characters)"
            value={password}
            onChange={e => setPassword(e.target.value)}
            minLength={6}
            required
          />

          {error && <div style={styles.error}>{error}</div>}

          <button style={styles.btn} type="submit" disabled={loading}>
            {loading ? 'Creating account...' : 'Create Account →'}
          </button>
        </form>

        <div style={styles.footer}>
          Already have an account?{' '}
          <Link to="/login" style={styles.link}>Log in</Link>
        </div>
      </div>
    </div>
  )
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#0e1f13',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    fontFamily: "'Barlow', sans-serif"
  },
  card: {
    background: '#1e3325',
    borderRadius: 24,
    padding: '48px 36px',
    width: '100%',
    maxWidth: 400,
    textAlign: 'center'
  },
  logo: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900,
    fontSize: 42,
    letterSpacing: 4,
    color: '#c8f135',
    marginBottom: 8
  },
  tagline: { fontSize: 14, color: '#6aad80', marginBottom: 36 },
  form: { display: 'flex', flexDirection: 'column', gap: 14 },
  input: {
    background: '#0e1f13',
    border: '1.5px solid #2a4a2e',
    borderRadius: 12,
    padding: '14px 16px',
    fontSize: 15,
    color: 'white',
    outline: 'none',
    fontFamily: "'Barlow', sans-serif"
  },
  error: {
    background: 'rgba(232,64,64,0.15)',
    border: '1px solid rgba(232,64,64,0.3)',
    color: '#f87171',
    borderRadius: 10,
    padding: '10px 14px',
    fontSize: 13
  },
  btn: {
    background: '#c8f135',
    color: '#0e1f13',
    border: 'none',
    borderRadius: 12,
    padding: '16px',
    fontSize: 16,
    fontWeight: 800,
    fontFamily: "'Barlow Condensed', sans-serif",
    letterSpacing: 1,
    cursor: 'pointer',
    marginTop: 4
  },
  footer: { marginTop: 28, fontSize: 13, color: '#6aad80' },
  link: { color: '#c8f135', fontWeight: 700, textDecoration: 'none' }
}
